import React, { useEffect, useState } from "react";

import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	useTheme,
	Button,
	Stack,
	Theme,
	LinearProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import OrganizationModal from "./InvoiceManagerOrgModal";
import InvoiceManagerEditor, { InvoiceData } from "./InvoiceManagerEditor";
import InvoiceApiServices from "../../services/hoa-invoice-api-services";
import hoaDatastore from "../../store/hoa-datastore";
import LoadingComponent from "../loading-component/loading-component";
import hoaConfig from '../../hoa-config';

interface Organization {
	id?: string;
	name?: string;
}
interface Invoice {
	id: string;
	updatedBy: string;
	organization: Organization;
	dataJson: InvoiceData;
	updatedAt: string | Date;
}

export default function InvoiceManagerDashboard() {
	const theme = useTheme();
	const [mode, setMode] = useState<"add" | "update" | null>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [invoicesData, setInvoicesData] = useState<Invoice[]>([]);
	const [loading, setLoading] = useState(false);
	const [selectedDataJson, updateSelectedDataJson] = useState<Invoice | null>(null);
	const [dropDownSelectedOrgId, updateDropDownSelectedOrgId] = useState<string | null>(null);
	const [invoiceData, setInvoiceData] = useState<InvoiceData>({
		hoa: {},
		property: {},
		event: {},
	});

	const handleCloseModal = () => {
		setIsOpen(false);
	};

	const resetAll = () => {
		updateSelectedDataJson(null);
		updateDropDownSelectedOrgId(null);
		setMode(null);
		setInvoiceData({
			hoa: {},
			property: {},
			event: {},
		});
	};
	const handleOpenModal = () => {
		resetAll();
		setIsOpen(true);
	};

	const handleBackClick = () => {
		handleCloseModal();
		resetAll();
	};

	const handleSaveClick = async () => {
		setLoading(true);
		if (mode === "add") {
			const cratedInvoice = await InvoiceApiServices.createInvoiceDataField(
				dropDownSelectedOrgId,
				invoiceData,
				hoaDatastore.ownerInformation.name
			);
			if(cratedInvoice) {
				invoicesData.push(cratedInvoice.data)
				setInvoicesData(invoicesData);
			}

		} else if (mode === "update") {
			const updatedData = await InvoiceApiServices.updateInvoiceDataField(
				selectedDataJson.id,
				invoiceData,
				hoaDatastore.ownerInformation.name
			);

			const index = invoicesData.findIndex(
				(invoice: Invoice) => invoice.id === selectedDataJson.id
			);
			invoicesData[index] = { ...updatedData.data };
			setInvoicesData(invoicesData);
		}
		console.log("Saved");
		setLoading(false);
		resetAll();
	};

	const fetchInvoiceConfigData = async () => {
		setLoading(true);
		const invoices = await InvoiceApiServices.getInvoiceDataServices();
		setInvoicesData(invoices.data);
		setLoading(false);
	};

	const onEditModeClick = (json: Invoice) => {
		setMode("update");
		updateSelectedDataJson(JSON.parse(JSON.stringify(json)));
	};

	const addNewModalClick = () => {
		setMode("add");
		updateSelectedDataJson(null);
	};

	const isOrganizationAlreadyExist = (orgId: string) => {
		return invoicesData.find((invoice) => invoice.organization.id === orgId);
	};

	const getOrganizationName = () => {
		if(mode == 'add') {
			return hoaConfig.hoaOrganizations.find((org:Organization) => org.id === dropDownSelectedOrgId)?.name
		} else if(mode == 'update') {
			return selectedDataJson.organization.name
		}
	}

	useEffect(() => {
		fetchInvoiceConfigData();
	}, []);

	if (mode) {
		return (
			<>
				<LoadingComponent loading={loading} />
				<InvoiceManagerTopBar
					onBackClick={handleBackClick}
					onSaveClick={handleSaveClick}
					theme={theme}
				/>
				<InvoiceManagerEditor
					mode={mode}
					data={selectedDataJson?.dataJson}
					invoiceData={invoiceData}
					setInvoiceData={setInvoiceData}
					organization={getOrganizationName()}
				/>
			</>
		);
	}

	return (
		<main>
			<section className="filtering-main-container">
				<div className="filtering-sub-container">
					<div className="filtering-container left">
						<span className="filtering-left-container-title">Invoice Data Mapping</span>
						<span className="filtering-left-container-subtitle">
							View & manage all the recently added Invoice Data mapping
						</span>
					</div>
					<Button
						variant="contained"
						onClick={handleOpenModal}
						startIcon={<AddIcon />}
						sx={{ height: 48, width: 130 }}
					>
						Add New
					</Button>
				</div>
			</section>

			<section className="invoice-table">
				<TableContainer elevation={0} component={Paper} sx={{ maxHeight: 400 }}>
					{loading && <LinearProgress />}
					<Table stickyHeader>
						<TableHead>
							<TableRow
								sx={{
									"& th": {
										backgroundColor: "#f5f5f5",
										borderBottom: `1px solid ${theme.palette.divider}`,
									},
								}}
							>
								<TableCell>Organization</TableCell>
								<TableCell>Updated At</TableCell>
								<TableCell>Updated By</TableCell>
							</TableRow>
						</TableHead>

						<TableBody>
							{invoicesData.map((invoice) => (
								<TableRow
									onClick={() => onEditModeClick(invoice)}
									id="table-body-tableRow"
									key={invoice.id}
								>
									<TableCell>{invoice.organization.name || "N/A"}</TableCell>
									<TableCell>
										{new Date(invoice.updatedAt).toLocaleDateString()}
									</TableCell>
									<TableCell>{invoice.updatedBy}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</section>

			<OrganizationModal
				isOpen={isOpen}
				onClose={handleCloseModal}
				onComplete={(orgId) => {
					if (!isOrganizationAlreadyExist(orgId)) {
						updateDropDownSelectedOrgId(orgId);
						addNewModalClick();
					} else {
						updateDropDownSelectedOrgId(null);
						window.alert("Organization Already Available in the list");
					}
				}}
			/>
		</main>
	);
}

interface InvoiceManagerTopBarProps {
	theme: Theme;
	onBackClick: () => void;
	onSaveClick: () => void;
}

const InvoiceManagerTopBar = ({ theme, onBackClick, onSaveClick }: InvoiceManagerTopBarProps) => (
	<Stack
		p={1}
		border={`1px solid ${theme.palette.divider}`}
		flexDirection="row"
		justifyContent="space-between"
	>
		<Button onClick={onBackClick} sx={{ width: "80px" }} variant="outlined">
			Back
		</Button>
		<Button onClick={onSaveClick} sx={{ width: "80px" }} variant="outlined">
			Save
		</Button>
	</Stack>
);
